// Agency-wide client complaint / grievance log (HFRD Rule 111-8-65-.12(3)).
// Aggregate compliance view. Complaints are logged per-client from the client
// profile "Complaints" tab; this page tracks and resolves them across all clients.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import DataTable from '../../components/DataTable';

const fmt = (d) => d ? new Date(d).toLocaleDateString('en-US') : '—';
const STATUS_STYLE = {
  open: { bg: 'var(--danger-bg)', fg: 'var(--danger)' },
  investigating: { bg: 'var(--warn-bg)', fg: 'var(--warn)' },
  resolved: { bg: 'var(--success-bg)', fg: 'var(--success)' },
};

const SOURCES = ['Client', 'Family / Responsible Party', 'Staff', 'Other'];
const CATEGORIES = ['Care Quality', 'Staff Conduct', 'Scheduling', 'Billing', 'Safety', 'Other'];
const EMPTY_LOG = { client_id: '', date_received: new Date().toISOString().slice(0, 10), source: 'Client', category: 'Care Quality', description: '', severity: 'medium' };

const tile = (label, value, color) => (
  <div style={{ flex: 1, minWidth: 120, background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 16px' }}>
    <div style={{ fontSize: 12, color: 'var(--text3)' }}>{label}</div>
    <div style={{ fontSize: 24, fontWeight: 700, color: color || 'var(--text)' }}>{value}</div>
  </div>
);

export default function Complaints() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [resolveRow, setResolveRow] = useState(null);
  const [resolution, setResolution] = useState('');
  const [busy, setBusy] = useState(false);
  const [clients, setClients] = useState([]);
  const [showLog, setShowLog] = useState(false);
  const [logForm, setLogForm] = useState(EMPTY_LOG);

  const load = useCallback(async () => {
    setLoading(true);
    const [{ data }, { data: cl }] = await Promise.all([
      supabase.from('client_complaints').select('*, clients(first_name, last_name, client_id)').order('date_received', { ascending: false }),
      supabase.from('clients').select('id, first_name, last_name, client_id').not('current_status', 'in', '("Discharged","Lost","Duplicate")').order('first_name'),
    ]);
    setRows(data || []); setClients(cl || []); setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const logComplaint = async () => {
    if (!logForm.client_id || !logForm.description.trim()) return;
    setBusy(true);
    await supabase.from('client_complaints').insert([{
      client_id: logForm.client_id, date_received: logForm.date_received, source: logForm.source,
      category: logForm.category, description: logForm.description.trim(), severity: logForm.severity,
      status: 'open', reported_by: profile?.full_name || null,
    }]);
    setBusy(false); setShowLog(false); setLogForm(EMPTY_LOG); load();
  };

  const overdue = (r) => r.status !== 'resolved' && r.due_date && new Date(r.due_date) < new Date();
  const counts = useMemo(() => ({
    open: rows.filter(r => r.status === 'open').length,
    investigating: rows.filter(r => r.status === 'investigating').length,
    resolved: rows.filter(r => r.status === 'resolved').length,
    overdue: rows.filter(overdue).length,
  }), [rows]);

  const filtered = useMemo(() => filter === 'all' ? rows
    : filter === 'overdue' ? rows.filter(overdue) : rows.filter(r => r.status === filter), [rows, filter]);

  const resolve = async () => {
    setBusy(true);
    await supabase.from('client_complaints').update({
      status: 'resolved', resolution: resolution.trim() || null,
      resolved_date: new Date().toISOString().slice(0, 10), resolved_by: profile?.full_name || null,
    }).eq('id', resolveRow.id);
    setBusy(false); setResolveRow(null); setResolution(''); load();
  };

  const columns = useMemo(() => [
    { id: 'reference_no', header: 'Ref #', size: 90, enableSorting: false, meta: { mobile: 'secondary' }, cell: i => <span style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--text2)' }}>{i.row.original.reference_no || '—'}</span> },
    { id: 'client', header: 'Client', size: 170, enableSorting: false, meta: { mobile: 'primary' }, cell: i => {
        const r = i.row.original; const c = r.clients;
        return c ? <button onClick={() => navigate(`/clients/${r.client_id}`)} style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', padding: 0, fontSize: 13 }}>{c.last_name}, {c.first_name}</button> : '—';
      } },
    { id: 'date_received', header: 'Received', size: 100, enableSorting: false, cell: i => fmt(i.row.original.date_received) },
    { id: 'category', header: 'Category', size: 120, enableSorting: false, cell: i => i.row.original.category || '—' },
    { id: 'description', header: 'Description', size: 280, enableSorting: false, cell: i => <span style={{ whiteSpace: 'normal' }}>{i.row.original.description}</span> },
    { id: 'status', header: 'Status', size: 130, enableSorting: false, meta: { mobile: 'primary' }, cell: i => {
        const r = i.row.original; const st = STATUS_STYLE[r.status] || STATUS_STYLE.open;
        return <span><span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, background: st.bg, color: st.fg }}>{r.status}</span>{overdue(r) && <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--danger)', fontWeight: 600 }}>⚠</span>}</span>;
      } },
    { id: 'actions', header: '', size: 100, enableSorting: false, meta: { mobile: 'secondary' }, cell: i => {
        const r = i.row.original;
        return r.status !== 'resolved'
          ? <button onClick={() => { setResolveRow(r); setResolution(''); }} style={{ background: 'none', border: '1px solid var(--accent)', color: 'var(--accent)', borderRadius: 5, padding: '4px 10px', cursor: 'pointer', fontSize: 12 }}>Resolve</button>
          : <span style={{ fontSize: 11, color: 'var(--text3)' }}>{fmt(r.resolved_date)}</span>;
      } },
  ], [navigate]);

  return (
    <div style={{ padding: 4 }}>
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        {tile('Open', counts.open, 'var(--danger)')}
        {tile('Investigating', counts.investigating, 'var(--warn)')}
        {tile('Overdue', counts.overdue, 'var(--danger)')}
        {tile('Resolved', counts.resolved, 'var(--success)')}
      </div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        {['all', 'open', 'investigating', 'overdue', 'resolved'].map(k => (
          <button key={k} onClick={() => setFilter(k)} style={{ textTransform: 'capitalize', padding: '6px 12px', borderRadius: 6, border: '1px solid var(--border)', cursor: 'pointer', fontSize: 13, background: filter === k ? 'var(--accent)' : 'var(--card-bg)', color: filter === k ? '#fff' : 'var(--text)' }}>{k}</button>
        ))}
        <button onClick={() => { setLogForm(EMPTY_LOG); setShowLog(true); }} style={{ marginLeft: 'auto', padding: '6px 14px', borderRadius: 6, border: 'none', background: 'var(--accent)', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>+ Log Complaint</button>
      </div>
      <p style={{ fontSize: 12, color: 'var(--text3)', margin: '0 0 12px' }}>Rule 111-8-65-.12(3) — complaints must be addressed in a timely manner (7-day target). You can log a complaint here or from a client's <b>Complaints</b> tab.</p>
      <DataTable tableId="all-complaints" columns={columns} data={filtered} getRowId={r => r.id}
        loading={loading} emptyMessage="No complaints logged." maxHeight="none" />

      {resolveRow && (
        <div onMouseDown={e => e.target === e.currentTarget && setResolveRow(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
          <div style={{ background: 'var(--card-bg)', borderRadius: 12, padding: 20, width: 460, maxWidth: '92vw' }}>
            <h3 style={{ marginTop: 0 }}>Resolve Complaint</h3>
            <p style={{ fontSize: 13, color: 'var(--text2)' }}>{resolveRow.description}</p>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Resolution / Action Taken</label>
            <textarea rows={4} value={resolution} onChange={e => setResolution(e.target.value)} placeholder="How was it resolved?"
              style={{ width: '100%', padding: 8, border: '1px solid var(--border)', borderRadius: 6, fontSize: 13, boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
              <button onClick={() => setResolveRow(null)} style={{ background: 'none', border: '1px solid var(--border2)', borderRadius: 6, padding: '8px 14px', cursor: 'pointer' }}>Cancel</button>
              <button onClick={resolve} disabled={busy} style={{ background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 14px', cursor: 'pointer', opacity: busy ? 0.6 : 1 }}>{busy ? 'Saving…' : 'Mark Resolved'}</button>
            </div>
          </div>
        </div>
      )}

      {showLog && (
        <div onMouseDown={e => e.target === e.currentTarget && setShowLog(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
          <div style={{ background: 'var(--card-bg)', borderRadius: 12, padding: 20, width: 480, maxWidth: '92vw', maxHeight: '88vh', overflowY: 'auto' }}>
            <h3 style={{ marginTop: 0 }}>Log a Complaint</h3>
            <label style={lbl}>Client *</label>
            <select value={logForm.client_id} onChange={e => setLogForm(f => ({ ...f, client_id: e.target.value }))} style={inp}>
              <option value="">Select client…</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.last_name}, {c.first_name} ({c.client_id})</option>)}
            </select>
            <div style={{ display: 'flex', gap: 12, marginTop: 10 }}>
              <div style={{ flex: 1 }}><label style={lbl}>Date received</label><input type="date" value={logForm.date_received} onChange={e => setLogForm(f => ({ ...f, date_received: e.target.value }))} style={inp} /></div>
              <div style={{ flex: 1 }}><label style={lbl}>Source</label><select value={logForm.source} onChange={e => setLogForm(f => ({ ...f, source: e.target.value }))} style={inp}>{SOURCES.map(o => <option key={o}>{o}</option>)}</select></div>
            </div>
            <div style={{ display: 'flex', gap: 12, marginTop: 10 }}>
              <div style={{ flex: 1 }}><label style={lbl}>Category</label><select value={logForm.category} onChange={e => setLogForm(f => ({ ...f, category: e.target.value }))} style={inp}>{CATEGORIES.map(o => <option key={o}>{o}</option>)}</select></div>
              <div style={{ flex: 1 }}><label style={lbl}>Severity</label><select value={logForm.severity} onChange={e => setLogForm(f => ({ ...f, severity: e.target.value }))} style={inp}><option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option></select></div>
            </div>
            <label style={{ ...lbl, marginTop: 10 }}>Description *</label>
            <textarea rows={4} value={logForm.description} onChange={e => setLogForm(f => ({ ...f, description: e.target.value }))} placeholder="What was the complaint?" style={{ ...inp, resize: 'vertical' }} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
              <button onClick={() => setShowLog(false)} style={{ background: 'none', border: '1px solid var(--border2)', borderRadius: 6, padding: '8px 14px', cursor: 'pointer' }}>Cancel</button>
              <button onClick={logComplaint} disabled={busy || !logForm.client_id || !logForm.description.trim()} style={{ background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 14px', cursor: 'pointer', opacity: (busy || !logForm.client_id || !logForm.description.trim()) ? 0.6 : 1 }}>{busy ? 'Saving…' : 'Log Complaint'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const lbl = { display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 };
const inp = { width: '100%', padding: 8, border: '1px solid var(--border)', borderRadius: 6, fontSize: 13, boxSizing: 'border-box', fontFamily: 'inherit' };
